/**
 * InsightChat - AI query input for asking questions about analytics data
 */

import { useState, useCallback } from 'react';

interface InsightChatProps {
  onAsk: (question: string) => void;
  isLoading?: boolean;
  suggestions?: string[];
}

const DEFAULT_SUGGESTIONS = [
  'Top 10 days for sessions',
  'Sessions by country',
  'Any anomalies?',
];

export function InsightChat({
  onAsk,
  isLoading = false,
  suggestions = DEFAULT_SUGGESTIONS,
}: InsightChatProps) {
  const [question, setQuestion] = useState('');

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (question.trim() && !isLoading) {
        onAsk(question.trim());
        setQuestion('');
      }
    },
    [question, isLoading, onAsk]
  );

  const handleSuggestionClick = useCallback(
    (suggestion: string) => {
      if (!isLoading) {
        onAsk(suggestion);
      }
    },
    [isLoading, onAsk]
  );

  return (
    <div className="border-t border-gray-200 pt-3 mt-3">
      {/* Suggestions */}
      <div className="flex flex-wrap gap-1.5 mb-2">
        {suggestions.map((suggestion) => (
          <button
            key={suggestion}
            onClick={() => handleSuggestionClick(suggestion)}
            disabled={isLoading}
            className="text-[10px] px-2 py-1 rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100 transition disabled:opacity-50"
          >
            {suggestion}
          </button>
        ))}
      </div>

      {/* Input */}
      <form onSubmit={handleSubmit} className="flex gap-2">
        <input
          type="text"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="e.g., Top 10 days, by country, anomalies..."
          disabled={isLoading}
          className="flex-1 px-3 py-2 text-sm rounded-lg border border-gray-300 bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
        />
        <button
          type="submit"
          disabled={!question.trim() || isLoading}
          className="px-3 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            'Ask'
          )}
        </button>
      </form>
    </div>
  );
}
